// order-management-system/frontend/src/helpers/UserSelect.js

import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Autocomplete, CircularProgress } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import debounce from 'lodash.debounce';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
const urlconfig = require('../config/urlconfig');

const UserSelect = ({ label, value, onChange, error, helperText }) => {
//  const { instance, accounts } = useMsal();
  const { instance, accounts } = useMsal(); // 2. Get `accounts` from useMsal
      const account = accounts && accounts.length > 0 ? accounts[0] : null; // 3. Define the active account from MSAL

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const fetchUsers = useMemo(
    () =>
      debounce(async (searchQuery, active) => {
        if (searchQuery.length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }

        if (!account) {
          console.error("No active account for fetching users.");
          setLoading(false);
          return;
        }

        try {
            const accessTokenResponse = await instance.acquireTokenSilent({
                     scopes: ["api://fdff9b73-dad5-40b1-b0e3-ef8396f39b72/access_as_user"],
                     account: account,
                   });
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/users?search=${encodeURIComponent(searchQuery)}`, {
                      headers: {
                        // This token is now correctly for your backend.
                        'Authorization': `Bearer ${accessTokenResponse.accessToken}`,
                      },
                    });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API error ${response.status}: ${errorText}`);
          }

          const users = await response.json();

          if (active) {
            setOptions(users.map(user => user.displayName || user.userPrincipalName || user.mail));
          }
        } catch (e) {
          if (error instanceof InteractionRequiredAuthError) {
              // Fallback to interactive token acquisition
              instance.acquireTokenRedirect({
                                  scopes: ["User.Read.All"],
                              });
            } else {
              console.error("Failed to fetch users:", error);
            }
        } finally {
            if (active) {
                setLoading(false);
            }
        }
      }, 400),
    [accounts, instance]
  );

  useEffect(() => {
    let active = true;

    if (!open) {
      setOptions([]);
      return undefined;
    }

    setLoading(true);
    fetchUsers(inputValue, active);

    return () => {
      active = false;
    };
  }, [open, inputValue, fetchUsers]);

  return (
    <Autocomplete
      id={`${label}-autocomplete`}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      value={value}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        onChange(newValue || '');
      }}
      isOptionEqualToValue={(option, val) => option === val}
      getOptionLabel={(option) => option || ''}
      filterOptions={(x) => x}
      autoComplete
      includeInputInList
      filterSelectedOptions
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default UserSelect;